import { CancelIcon } from '@/assets/icons'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import { EventCategory } from '@orbit/api-types'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import {
    ButtonComponent,
    ButtonGroupComponent,
    FormGroupComponent,
    FormInputComponent,
    FormSelectComponent,
    FormTextareaComponent,
} from './FormComponents'
import { LinkInputWrapper, RemoveLinkButton, TimePickerWrapper } from './FormComponents.styles'

const EVENT_CATEGORIES: EventCategory[] = [
	'FoodAndDrink',
	'SeeAndDo',
	'Nature',
	'Shopping',
	'Accommodation',
	'Transportation',
	'Event',
	'Other',
]

const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Events',
	Other: 'Other',
}

interface EventFormProps {
	title: string
	description: string
	category: EventCategory | undefined
	locationPrimaryText: string
	locationSecondaryText?: string
	links: string[]
	startTime?: string | null
	onTitleChange: (title: string) => void
	onDescriptionChange: (description: string) => void
	onCategoryChange: (category: EventCategory | undefined) => void
	onLocationPrimaryTextChange: (locationPrimaryText: string) => void
	onLocationSelect: (place: PlaceSuggestion) => void
	onLinksChange: (links: string[]) => void
	onStartTimeChange: (startTime: string) => void
	onSubmit: (e: React.FormEvent) => void
	onCancel: () => void
	submitButtonText: string
	submitDisabled?: boolean
}

const EventForm: React.FC<EventFormProps> = ({
	title,
	description,
	category,
	locationPrimaryText,
	locationSecondaryText,
	links,
	startTime,
	onTitleChange,
	onDescriptionChange,
	onCategoryChange,
	onLocationPrimaryTextChange,
	onLocationSelect,
	onLinksChange,
	onStartTimeChange,
	onSubmit,
	onCancel,
	submitButtonText,
	submitDisabled = false,
}) => {
	const handleLinkChange = (index: number, value: string) => {
		const newLinks = [...links]
		newLinks[index] = value
		onLinksChange(newLinks)
	}

	const handleAddLink = () => {
		onLinksChange([...links, ''])
	}

	const handleRemoveLink = (index: number) => {
		const newLinks = links.filter((_, i) => i !== index)
		onLinksChange(newLinks)
	}
	const footer = (
		<form onSubmit={onSubmit}>
			<ButtonGroupComponent>
				<ButtonComponent type="button" variant="secondary" onClick={onCancel}>
					Cancel
				</ButtonComponent>
				<ButtonComponent type="submit" variant="primary" disabled={submitDisabled}>
					{submitButtonText}
				</ButtonComponent>
			</ButtonGroupComponent>
		</form>
	)

	return (
		<>
		<FormGroupComponent label="Location (Optional)" htmlFor="event-location">
			<LocationAutocomplete
				id="event-location"
				value={locationPrimaryText}
				onChange={onLocationPrimaryTextChange}
				onPlaceSelect={onLocationSelect}
				placeholder="Search for a location..."
				autoFocus
			/>
			{locationSecondaryText && (
				<div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', paddingLeft: '0.25rem' }}>
					{locationSecondaryText}
				</div>
			)}
		</FormGroupComponent>
			<FormInputComponent
				label="Event Title"
				htmlFor="event-title"
				id="event-title"
				type="text"
				value={title}
				onChange={e => onTitleChange(e.target.value)}
				placeholder="Enter event title..."
				required
			/>
			<FormSelectComponent
				label="Category"
				htmlFor="event-category"
				id="event-category"
				value={category || EVENT_CATEGORIES[0]}
				onChange={e => onCategoryChange(e.target.value as EventCategory)}
			>
				{EVENT_CATEGORIES.map(cat => (
					<option key={cat} value={cat}>
						{EVENT_CATEGORY_LABELS[cat]}
					</option>
				))}
			</FormSelectComponent>
			<FormGroupComponent label="Start Time (Optional)" htmlFor="event-start-time">
				<TimePickerWrapper>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TimePicker
							value={startTime ? dayjs(`2000-01-01T${startTime}`) : null}
							onChange={(newValue: Dayjs | null) => {
								if (newValue) {
									onStartTimeChange(newValue.format('HH:mm'))
								} else {
									onStartTimeChange('')
								}
							}}
							format="HH:mm"
							slotProps={{
								textField: {
									placeholder: 'Select time',
									fullWidth: true,
								},
							}}
						/>
					</LocalizationProvider>
				</TimePickerWrapper>
			</FormGroupComponent>
			<FormTextareaComponent
				label="Description (Optional)"
				htmlFor="event-description"
				id="event-description"
				value={description}
				onChange={e => onDescriptionChange(e.target.value)}
				placeholder="Enter event description..."
				rows={3}
			/>
			<FormGroupComponent label="Links (Optional)" htmlFor="event-links">
				{links.length > 0 && (
					<div style={{ marginBottom: '0.5rem' }}>
						{links.map((link, index) => (
							<LinkInputWrapper key={index}>
								<input
									type="text"
									placeholder="Enter a link (e.g., https://example.com)"
									value={link}
									onChange={e => handleLinkChange(index, e.target.value)}
									autoComplete="off"
									style={{
										flex: 1,
										padding: '0.5rem',
										border: '1px solid #d1d5db',
										borderRadius: '6px',
										fontSize: '0.875rem',
									}}
								/>
								<RemoveLinkButton
									type="button"
									onClick={() => handleRemoveLink(index)}
									title="Remove link"
								>
									<CancelIcon width={18} height={18} fill="currentColor" />
								</RemoveLinkButton>
							</LinkInputWrapper>
						))}
					</div>
				)}
				<button
					type="button"
					onClick={handleAddLink}
					style={{
						background: 'white',
						color: '#374151',
						border: '1px solid #d1d5db',
						borderRadius: '6px',
						padding: '0.5rem 1rem',
						cursor: 'pointer',
						fontSize: '0.875rem',
						fontWeight: '500',
						width: '100%',
						marginBottom: '1rem',
					}}
				>
					+ Add Link
				</button>
			</FormGroupComponent>
			{footer}
		</>
	)
}

export default EventForm
